
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex';
import FilterCatalog from '~/components/catalog/filterCatalog';
import CustomSelect from '~/components/elements/CustomSelect';
import Button from '~/components/elements/Button';
import ProductsLazyLoad from '~/components/catalog/productsLazyLoad';
import {productGroupTypes} from '@/plugins/enums/product';

import { phraseDeclension } from '@/plugins/formatting';
import {sortOptionsList} from '~/plugins/enums/sort';
import {computeFilterData, concatCatalogRoutePath, generateCategoryUrl} from '~/plugins/helpers/catalog';
import Banner from '~/components/catalog/banner';
import VSticky from '~/components/elements/VSticky/VSticky';
import Tag from '~/components/catalog/tag';
import CustomPagination from '~/components/elements/CustomPagination';

export default {
  name: 'CatalogWrapper',
  components: {
    CustomPagination,
    Tag,
    VSticky,
    Banner,
    ProductsLazyLoad,
    Button,
    CustomSelect,
    FilterCatalog,
  },
  props: {
    catalogType: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
    filterEnabled: {
      type: Boolean,
      default: true,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    banners: {
      type: Object,
      default: null,
    },
    defaultFilter: {
      type: Object,
      default: () => {},
    },
    excludedFilters: {
      type: Array,
      default: () => [],
    },
    defaultProducts: {
      type: Object,
      required: true,
    },
    filter: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const currentSortOption = sortOptionsList.find((option) => {
      const isEqualOrderField = option.value === this.$route.query.orderField;
      const isEqualOrderDirection = option.direction === this.$route.query.orderDirection;

      if (this.$route.query.orderField && this.$route.query.orderDirection) {
        return isEqualOrderField && isEqualOrderDirection;
      } else if (this.$route.query.orderField || this.$route.query.orderDirection) {
        return isEqualOrderField || isEqualOrderDirection;
      }

      return option.id === 1;
    });

    const defaultFilterParams = {
      orderField: 'popularity',
      orderDirection: 'desc',
    };

    return {
      isOpenedFilter: false,
      totalProducts: this.defaultProducts?.range || 0,
      currentSortOption,
      defaultFilterParams,
      sortOptionsList,
      paramsFetchItems: null,
      products: { ...this.defaultProducts },

      bannerRange: 4,
      currentBanner: 0,
      productsPerPage: this.defaultProducts?.limit || 12,
      isCatalogLoading: false,
    }
  },
  computed: {
    ...mapState('route', {
      categoryCode: (state) => state.params.category || '',
      tagCode: (state) => state.params.tag || '',
      entityCode: (state) => state.params.entityCode,
    }),
    ...mapState('cart', {
      addingCartLoading: 'addingCartLoading',
    }),
    ...mapState('catalog', {
      productGroup: 'productGroup',
      currentItemsFilters: 'currentItemsFilters',
      allCategories: 'allCategories',
    }),
    ...mapGetters('catalog', ['activeTags', 'routeSegments', 'categoryTags']),
    mqIsMobile() {
      return this.$mq === 'mobileXs' || this.$mq === 'mobile' || this.$mq === 'tablet';
    },
    currentPage() {
      return +this.$route.query?.page || 1
    },
    visibleFilter() {
      return this.mqIsMobile ? this.isOpenedFilter : true;
    },
    totalProductsPhrase() {
      // Ограничение Elasticsearch
      if (this.totalProducts === 10000) {
        return 'более 10000 товаров'
      }

      return this.phraseDeclension(this.totalProducts, ['товар', 'товаров', 'товара']);
    },
    filterCurrentCount() {
      return this.activeTags.length;
    },
    routeCatalogWithoutFilters() {
      const { entityCode, categoryCode, catalogType, tagCode } = this;
      const query = { ...this.$route.query };

      return {
        path: generateCategoryUrl(catalogType, entityCode, categoryCode, tagCode),
        query,
      }
    },
    isBannerTop() {
      return this.banners?.top?.length
    },
    isBannerItem() {
      return this.banners?.item?.length
    },
    isWrapperCatalog() {
      return this.catalogType !== productGroupTypes.CATALOG
    },
    getSortOptionsList() {
      return sortOptionsList.filter((option) => (
        !(option.hideOn ?? []).includes('catalog')
      ));
    },
  },
  watch: {
    '$route.query'() {
      this.currentSortOption = sortOptionsList.find((option) => {
        const isEqualOrderField = option.value === this.$route.query.orderField;
        const isEqualOrderDirection = option.direction === this.$route.query.orderDirection;

        if (this.$route.query.orderField && this.$route.query.orderDirection) {
          return isEqualOrderField && isEqualOrderDirection;
        } else if (this.$route.query.orderField || this.$route.query.orderDirection) {
          return isEqualOrderField || isEqualOrderDirection;
        }

        return option.id === 1;
      });
    },
    currentSortOption(newSortParam) {
      const itemsFiltersURL = {
        orderField: newSortParam.value !== this.defaultFilterParams.orderField ? newSortParam.value : undefined,
        orderDirection:
          newSortParam.direction !== this.defaultFilterParams.orderDirection ? newSortParam.direction : undefined,
      };

      this.$router.replace({ query: itemsFiltersURL });
      this.fetchCatalogProducts()
    },
    defaultProducts() {
      this.currentBanner = 0
      this.products = { ...this.defaultProducts }
      this.totalProducts = this.defaultProducts.range;

      this.updateFilter()
    },
    filter() {
      this.setFilters(this.filter)
    },
  },
  beforeDestroy() {
    this.currentBanner = 0
  },
  created() {
    this.setFilters(this.filter)
  },
  beforeMount() {
    this.updateFilter()
  },
  mounted() {
    this.currentBanner = 0
  },
  methods: {
    ...mapActions('catalog', {
      updateFilters: 'updateFilters',
      fetchFilters: 'fetchFilters',
    }),
    ...mapMutations('catalog', {
      setFilters: 'SET_FILTERS',
    }),
    phraseDeclension,
    routeTagGenerator(tag) {
      const { entityCode, categoryCode } = this;
      const query = { ...this.$route.query };

      return {
        path: generateCategoryUrl('catalog', entityCode, categoryCode, tag),
        query,
      }
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    },
    async onShowMore({ page }) {
      const query = { ...this.$route.query, page }

      if (query.page === 1) {
        delete query.page
      }

      await this.$router.replace({ path: this.$route.path, query })
      await this.fetchCatalogProducts(page, true)
    },
   async pageSelectedHandler({ page }) {
      const query = { ...this.$route.query, page }

     if (query.page === 1) {
       delete query.page
     }

      await this.$router.push({ path: this.$route.path, query })
      await this.fetchCatalogProducts(page)

     setTimeout(() => this.scrollToTop(), 1)
    },
    closeFilter() {
      this.isOpenedFilter = false;
    },
    openFilter() {
      this.isOpenedFilter = true;
    },
    onDeleteTag(value) {
      const { routeSegments, catalogType, entityCode, categoryCode } = this;

      if (!routeSegments.includes(value)) {
        return;
      } else {
        const index = routeSegments.indexOf(value);
        if (index !== -1) routeSegments.splice(index, 1);
      }

      const path = concatCatalogRoutePath(catalogType, entityCode, categoryCode, routeSegments);
      const query = { ...this.$route.query };
      delete query.page;

      this.$router.replace({ path, query });
    },
    addBannersToProducts(products) {
      if (!this.isBannerItem || this.currentBanner > this.banners.length) return products

      const arr = []
      const bannerPos = this.getBannerPos(products.length)

      products.forEach((product, index) => {
        arr.push(product)

        if (this.isBannerOrder(index, bannerPos) && this.currentBanner < this.banners.item.length) {
          arr.push({
            ...this.banners.item[this.currentBanner++],
            isBannerItem: true,
          })
        }
      })

      if (this.currentBanner < this.banners.item.length) {
        const left = this.banners.item.slice(this.currentBanner)

        left.forEach(item => {
          arr.push({
            ...item,
            isBannerItem: true,
          })
        })
      }

      return arr
    },
    getBannerPos(len) {
      return len > this.bannerRange ? this.bannerRange : len
    },
    isBannerOrder(index, bannerPos) {
      return !((index + 1) % bannerPos)
    },
    updateFilter(page = 1) {
      const filterPayload = {
        code: this.$route.params.category,
        product_group: this.$route.params.entityCode,
        pathMatch: this.$route.params.pathMatch,
      };
      if (this.$route.name.indexOf('brands.brand') === 0) {
        delete filterPayload.product_group;
        filterPayload.brand = this.$route.params.entityCode;
      }
      const {filter} = computeFilterData(filterPayload);

      this.paramsFetchItems = {
        page,
        filter,
        orderField: this.currentSortOption.value,
        orderDirection: this.currentSortOption.direction,
      };
      this.updateFilters(this.paramsFetchItems);
    },
    async fetchCatalogProducts(page = 1, add = false) {
      try {
        this.isCatalogLoading = true
        this.updateFilter(page)
        const {items, range} = await this.$api.catalog.getCatalogItems(this.currentItemsFilters);

        this.products.range = range

        add
          ? this.products.items.push(...this.addBannersToProducts(items))
          : this.products.items = this.addBannersToProducts(items)

        this.totalProducts = range;
      } catch (error) {
        console.error(error);
        this.products = {
          items: [],
          range: 0,
        };
        this.totalProducts = 0;
      } finally {
        this.isCatalogLoading = false
      }
    },
  },
}
